import { animate, createTimeline, stagger } from '../../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, resolveDuration, resolveStagger, SUPPORTS_CLIP, BLUR } from '../motion-tokens.js';

export const galleryV1 = (el) => {
    // v1: masonry — Stagger Grid Cascade bidimensional con cálculo de matriz en tiempo de ejecución
    const items = el.querySelectorAll('.gallery-item, .masonry-item, [class*="gallery-item"]');
    if (!items.length) return null;

    // Calcular el grid de forma dinámica en base a offsets reales del DOM (responsivo puro)
    const offsetsX = new Set();
    const offsetsY = new Set();
    items.forEach(item => {
        offsetsX.add(item.offsetLeft);
        offsetsY.add(item.offsetTop);
    });

    const cols = offsetsX.size || 1;
    const rows = offsetsY.size || 1;

    return animate(items, {
        opacity:    [0, 1],
        scale:      [0.85, 1],
        translateY: [45, 0],
        filter:     ['blur(4px)', 'blur(0px)'],
        delay:      stagger(90, { grid: [cols, rows], from: 'first' }),
        duration:   resolveDuration(DURATION.slow),
        ease:       SPRING_PRESETS.deluxe,
    });
};

export const galleryV2 = (el) => {
    // v2: slider/Splide — Splide maneja la transición interna.
    // SOLO fade-in del wrapper para no conflictar con Splide.
    const wrapper = el.querySelector('.splide, .gallery-slider, .gallery-wrapper');
    if (!wrapper) return null;

    return animate(wrapper, {
        opacity:  [0, 1],
        duration: resolveDuration(DURATION.normal),
        ease:     EASE.refined,
    });
};

export const galleryV3 = (el) => {
    // v3: featured grid — PRIMARIO: featured escala desde el centro.
    // SECUNDARIO: items pequeños stagger alrededor.
    const featured  = el.querySelector('.gallery-featured, .gallery-main, .gallery-item:first-child');
    const smalls    = [...el.querySelectorAll('.gallery-item, .gallery-thumb')].filter(i => i !== featured);
    const tl = createTimeline({ defaults: { ease: EASE.snappy }, });

    // Primario: featured desde el centro
    if (featured) {
        tl.add(featured, {
            opacity:    [0, 1],
            scale:      [0.88, 1],
            filter:     ['blur(4px)', 'blur(0px)'],
            duration:   resolveDuration(DURATION.slow),
            ease:       EASE.refined,
        });
    }

    // Secundario: miniaturas con stagger
    if (smalls.length) {
        tl.add(smalls, {
            opacity:    [0, 1],
            scale:      [0.85, 1],
            filter:     ['blur(3px)', 'blur(0px)'],
            delay:      stagger(resolveStagger(STAGGER.fast)),
            duration:   resolveDuration(DURATION.normal),
        }, featured ? '-=300' : 0);
    }

    return tl;
};

/**
 * v4 "Banner editorial": el render reutiliza el DOM de v3 (.gallery-featured) pero el CSS lo
 * re-maqueta en VERTICAL — banner a todo lo ancho arriba + tira de miniaturas debajo — así
 * que la coreografía es propia y no un alias de v3 (que abre desde el centro, propio del
 * layout destacada-izquierda + grid-derecha).
 *
 * PRIMARIO: el banner se destapa como una cortina (clipPath izq→der, con degradación a fade
 * donde no haya soporte — mismo guard que logosV1) mientras su imagen entra con un empuje
 * de escala tipo Ken Burns. SECUNDARIO: la tira de miniaturas entra en cascada lateral,
 * como una película pasando. TERCIARIO: el pie de foto (visible siempre en v4) sube al final.
 */
export const galleryV4 = (el) => {
    const wrap = el.querySelector('.gallery-featured');
    const piezas = [...el.querySelectorAll('.gallery-item')];
    if (!piezas.length) return null;

    // El banner es la pieza del PRIMER hijo del wrapper (el render lo pinta ahí). Sin
    // wrapper (DOM inesperado) cae a la primera pieza: nunca se queda sin animar.
    const banner = (wrap && wrap.children[0] && wrap.children[0].querySelector('.gallery-item'))
        || piezas[0];
    const tira = piezas.filter(p => p !== banner);
    const pie = banner.querySelector('.caption-overlay');

    // Estado inicial forzado (previene el flash antes del primer frame).
    banner.style.opacity = '0';
    if (SUPPORTS_CLIP) banner.style.clipPath = 'inset(0 100% 0 0)';
    tira.forEach(t => { t.style.opacity = '0'; t.style.transform = 'translateX(-28px) scale(0.94)'; });
    if (pie) { pie.style.opacity = '0'; pie.style.transform = 'translateY(14px)'; }

    const tl = createTimeline({ defaults: { ease: EASE.refined } });

    tl.add(banner, SUPPORTS_CLIP ? {
        clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
        opacity:  [0, 1],
        duration: resolveDuration(DURATION.slow),
        ease:     EASE.snappy,
    } : {
        opacity:  [0, 1],
        scale:    [0.96, 1],
        filter:   [BLUR.heavy, BLUR.none],
        duration: resolveDuration(DURATION.slow),
    }, 0);

    const foto = banner.querySelector('img');
    if (foto) tl.add(foto, {
        scale:    [1.12, 1],
        duration: resolveDuration(DURATION.slow * 1.3),
        ease:     SPRING_PRESETS.cinematic,
    }, 0);

    if (tira.length) tl.add(tira, {
        opacity:    [0, 1],
        translateX: [-28, 0],
        scale:      [0.94, 1],
        filter:     [BLUR.light, BLUR.none],
        delay:      stagger(resolveStagger(STAGGER.fast)),
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.deluxe,
    }, '-=700');

    if (pie) tl.add(pie, {
        opacity:    [0, 1],
        translateY: [14, 0],
        duration:   resolveDuration(DURATION.fast),
    }, '-=600');

    return tl;
};
